import { useState, useEffect, useRef } from 'react';

interface PartnerFormModalProps {
  partner?: { id: number; name: string; logo: string; website: string; order: number; visible: boolean } | null;
  onClose: () => void;
  onSave: (data: { name: string; logo: string; website: string }) => void;
}

export default function PartnerFormModal({ partner, onClose, onSave }: PartnerFormModalProps) {
  const [form, setForm] = useState({ name: '', logo: '', website: '' });
  const [previewUrl, setPreviewUrl] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [tab, setTab] = useState<'url' | 'upload'>('url');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (partner) {
      setForm({ name: partner.name, logo: partner.logo, website: partner.website });
      setPreviewUrl(partner.logo);
      setUrlInput(partner.logo);
    }
  }, [partner]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUrlApply = () => {
    setPreviewUrl(urlInput);
    setForm((prev) => ({ ...prev, logo: urlInput }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result as string;
      setPreviewUrl(result);
      setForm((prev) => ({ ...prev, logo: result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name) return;
    onSave(form);
  };

  const inputClass = "w-full px-4 py-2.5 text-sm rounded-xl border-2 border-rose-100 bg-rose-50/30 focus:outline-none focus:border-rose-300 transition-all";
  const labelClass = "block text-xs font-semibold text-gray-600 mb-1.5";

  return (
    <div className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center p-4" dir="rtl">
      <div className="bg-white rounded-2xl border-2 border-rose-100 w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-rose-100 sticky top-0 bg-white z-10">
          <h2 className="font-bold text-gray-800 text-base">
            {partner ? 'עריכת שותף' : 'הוספת שותף חדש'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl border-2 border-gray-200 text-gray-400 hover:bg-gray-50 cursor-pointer transition-all"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Logo upload section */}
          <div>
            <label className={labelClass}>לוגו</label>

            {/* Tab switcher */}
            <div className="flex items-center gap-1 bg-rose-50 border-2 border-rose-100 rounded-xl p-1 mb-3">
              <button
                type="button"
                onClick={() => setTab('url')}
                className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  tab === 'url' ? 'bg-white text-rose-500 border-2 border-rose-200' : 'text-gray-400 border-2 border-transparent'
                }`}
              >
                קישור URL
              </button>
              <button
                type="button"
                onClick={() => setTab('upload')}
                className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  tab === 'upload' ? 'bg-white text-rose-500 border-2 border-rose-200' : 'text-gray-400 border-2 border-transparent'
                }`}
              >
                העלאת קובץ
              </button>
            </div>

            {tab === 'url' ? (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://example.com/logo.png"
                  className={`${inputClass} flex-1`}
                />
                <button
                  type="button"
                  onClick={handleUrlApply}
                  className="px-4 py-2.5 rounded-xl bg-rose-400 text-white text-xs font-bold hover:bg-rose-500 transition-all cursor-pointer whitespace-nowrap"
                >
                  אישור
                </button>
              </div>
            ) : (
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full py-3 rounded-xl border-2 border-dashed border-rose-200 bg-rose-50/50 text-rose-400 text-sm font-semibold hover:bg-rose-50 transition-all cursor-pointer flex items-center justify-center gap-2"
                >
                  <i className="ri-upload-cloud-line text-lg"></i>
                  לחץ להעלאת לוגו
                </button>
              </div>
            )}

            {/* Preview */}
            {previewUrl && (
              <div className="mt-3 h-24 rounded-xl border-2 border-rose-100 bg-rose-50/30 overflow-hidden flex items-center justify-center">
                <img
                  src={previewUrl}
                  alt="preview"
                  className="max-h-full max-w-full object-contain p-2"
                  onError={() => setPreviewUrl('')}
                />
              </div>
            )}
          </div>

          {/* Name */}
          <div>
            <label className={labelClass}>שם השותף *</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              placeholder="שם החברה / ארגון..."
              className={inputClass}
            />
          </div>

          {/* Website */}
          <div>
            <label className={labelClass}>אתר אינטרנט</label>
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={handleChange}
              placeholder="https://..."
              className={inputClass}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              className="flex-1 py-2.5 rounded-xl bg-rose-400 text-white text-sm font-bold hover:bg-rose-500 transition-all cursor-pointer whitespace-nowrap"
            >
              {partner ? 'שמור שינויים' : 'הוסף שותף'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
            >
              ביטול
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
